import React, { useMemo } from 'react';
import { HashRouter, Route, Routes } from 'react-router-dom';
import MyPagesMobileMenu from './MyPagesMobileMenu';
import MyPagesDesktopMenu from './MyPagesDesktopMenu';
import MyPagesSettings from './MyPagesSettings';
import MyPagesOrderHistory from './MyPagesOrderHistory';
import MyPagesOrderDetail from './MyPagesOrderDetail';
import MyPagesUsers from './MyPagesUsers';
import MyPagesAddUser from './MyPagesAddUser';
import MyPagesEditUser from './MyPagesEditUser';
import MyPagesAddresses from './MyPagesAddresses';
import MyPagesAddAddress from './MyPagesAddAddress';
import MyPagesEditAddress from './MyPagesEditAddress';
import { translate } from '../../Services/translation';
import ScrollToTop from './ui/ScrollToTop';
import { useTypedSelector } from '../../Hooks/useTypedSelector';
import { EditorStringModel } from '../../Types/EditorString';
import { MyPagesInterface } from './Types/MyPages';
import MyPagesReturnForm from './MyPagesReturnForm';

const MyPagesContainer = (props: MyPagesInterface) => {
  const { current: userInfo } = useTypedSelector(
    (state) => state.userInfo
  );

  const { detailsIntro, user, order, address, returnForm } = props;

  const navigation = useMemo(
    () => [
      {
        name: translate('mypage.info.title'),
        url: '/',
        isB2bOnly: false,
        active: true,
      },
      {
        name: translate('mypage.order.title'),
        url: '/orders',
        isB2bOnly: false,
        active: true,
      },
      {
        name: translate('mypages-menu.order-return'),
        url: '/return',
        isB2bOnly: true,
        active: returnForm.active,
      },
      {
        name: translate('mypage.person.title'),
        url: '/users',
        isB2bOnly: true,
        active: true,
      },
      {
        name: translate('mypage.address.addresses'),
        url: '/addresses',
        isB2bOnly: true,
        active: true,
      },
    ],
    [returnForm.active]
  );

  const filteredNav = useMemo(
    () =>
      navigation.filter((option) =>
        !userInfo?.isBusinessCustomer ? !option.isB2bOnly : true
      ),
    [userInfo?.isBusinessCustomer]
  );

  return (
    <section className="container container--narrow pb-6 pt-4 text-gray-900 lg:grid lg:grid-cols-4 lg:gap-12 lg:pb-10 lg:pt-10">
      <HashRouter>
        <ScrollToTop />
        <MyPagesMobileMenu navigation={filteredNav} />
        <MyPagesDesktopMenu navigation={filteredNav} />
        <Routes>
          <Route
            path="/"
            element={<MyPagesSettings detailsIntro={detailsIntro} />}
          />
          <Route
            path="/orders"
            element={<MyPagesOrderHistory {...order} />}
          />

          <Route path="/orders/:id" element={<MyPagesOrderDetail />} />

          {userInfo?.isBusinessCustomer && (
            <>
              {returnForm.active && (
                <Route
                  path="/return"
                  element={<MyPagesReturnForm {...returnForm} />}
                />
              )}
              <Route
                path="/users"
                element={<MyPagesUsers description={user.description} />}
              />
              <Route
                path="/users/add"
                element={
                  <MyPagesAddUser description={user.addUserDescription} />
                }
              />
              <Route
                path="/users/edit/:id"
                element={
                  <MyPagesEditUser
                    description={user.editUserDescription}
                  />
                }
              />
              <Route
                path="/addresses"
                element={
                  <MyPagesAddresses description={address.description} />
                }
              />
              <Route
                path="/addresses/add"
                element={
                  <MyPagesAddAddress
                    description={address.addAddressDescription}
                  />
                }
              />
              <Route
                path="/addresses/edit/:id"
                element={
                  <MyPagesEditAddress
                    description={address.editAddressDescription}
                  />
                }
              />
            </>
          )}
        </Routes>
      </HashRouter>
    </section>
  );
};

export default MyPagesContainer;
